import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  TableContainer,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  SkeletonText,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { dateFormat, FORMATS, getRowIndex } from "utils";
import Pagination from "components/Pagination";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE, MerchantViewRef } from "common";
import DatePicker from "components/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { useHistory } from "react-router-dom";
import {  CopyIcon } from "@chakra-ui/icons";
import { MerchantIcon, PaymentIcon } from "components/Icons";
import styles from "./TransactionHistory.module.css";
import { GetTransactionsRequest } from "redux/services/wallet/types";
import { useGetTransactionHistoryQuery } from "redux/services/account";
import { min } from "lodash";

const TransactionHistoryView: React.FC = () => {
  const { push } = useHistory();

  const [
    getTransactionsHistoryParamsRequest,
    setGetTransactionsHistoryParamsRequest,
  ] = useState<
    GetTransactionsRequest & {
      localFromTime?: Dayjs;
      localToTime?: Dayjs;
      pageNumber: number;
    }
  >({
    pageNumber: DEFAULT_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    localToTime: dayjs().endOf("day"),
    localFromTime: dayjs().subtract(90, "day").startOf("day"),
  });

  const { localFromTime, localToTime, ...paymentQuery } =
    getTransactionsHistoryParamsRequest;

  const {
    data: transactionsHistoryData,
    isFetching: isGetOrganisationsFetching,
    isLoading: isLoadingOrganisations,
    isError: isGetOrganisationsError,
  } = useGetTransactionHistoryQuery(
    {
      fromDateTime: localFromTime
        ? localFromTime
            .startOf("day")
            .format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
        : undefined,
      toDateTime: localToTime
        ? localToTime.endOf("day").format(FORMATS["YYYY-MM-DDTHH:mm:ss"])
        : undefined,
      ...paymentQuery,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );


  const isLoading = isLoadingOrganisations || isGetOrganisationsFetching;

  return (
    <Flex
      className="Users"
      w="100%"
      h="100%"
      p="20px"
      flexDir={"column"}
      position="relative"
      minHeight="calc(100vh - 124px)"
    >
      <Text textStyle={"h1"} fontWeight={"extrabold"}>
        View Transaction History
      </Text>

      <div className="grid my-5 gap-5 lg:grid-cols-2">
        <Box
          borderColor={"transparent"}
          paddingTop={"5px"}
          paddingBottom={"5px"}
        >
          <Box flexDirection={"row"} display={"flex"}>
            <Text
              alignSelf={"center"}
              textStyle={"h4"}
              fontSize={"14px"}
              className={styles.filterLabel}
              w={"50px"}
            >
              Transaction Date From
            </Text>
            <Box w={"100%"}>
              <DatePicker
                isDisableClear  
                maxDate={dayjs(getTransactionsHistoryParamsRequest?.localToTime).toDate()}
                minDate={dayjs(getTransactionsHistoryParamsRequest?.localToTime)
                  .subtract(180, "day")
                  .toDate()}
                selected={
                  getTransactionsHistoryParamsRequest?.localFromTime
                    ? dayjs(
                        getTransactionsHistoryParamsRequest?.localFromTime
                      ).toDate()
                    : undefined
                }
                onChange={(date) => {
                  if (!date) {
                    // clear date from
                    setGetTransactionsHistoryParamsRequest({
                      ...getTransactionsHistoryParamsRequest,
                      pageNumber: DEFAULT_PAGE,
                      localFromTime: undefined,
                    });
                  } else {
                    const fromDateTime = dayjs(date).startOf("day");
                    const toDateTime = dayjs(
                      getTransactionsHistoryParamsRequest.localToTime
                    ).endOf("day");

                    const isSelectDateAfterToDate =
                      fromDateTime.valueOf() >= toDateTime.valueOf();

                    setGetTransactionsHistoryParamsRequest({
                      ...getTransactionsHistoryParamsRequest,
                      pageNumber: DEFAULT_PAGE,
                      localFromTime: fromDateTime,
                      localToTime: isSelectDateAfterToDate
                        ? fromDateTime.clone().add(1, "day").endOf("day")
                        : getTransactionsHistoryParamsRequest?.localToTime,
                    });
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        <Box
          borderColor={"transparent"}
          paddingTop={"5px"}
          paddingBottom={"5px"}
        >
          <Box flexDirection={"row"} display={"flex"}>
            <Text
              alignSelf={"center"}
              textStyle={"h4"}
              fontSize={"14px"}
              className={styles.filterLabel}
            >
              Transaction Date To
            </Text>
            <Box w={"100%"}>
              <DatePicker
                isDisableClear
                maxDate={min([
                  dayjs(getTransactionsHistoryParamsRequest?.localFromTime)
                    .add(180, "day")
                    .endOf("day")
                    .toDate(),
                  dayjs().endOf("day").toDate(),
                ])}
                minDate={dayjs(
                  getTransactionsHistoryParamsRequest?.localFromTime
                ).toDate()}
                selected={
                  getTransactionsHistoryParamsRequest?.localToTime
                    ? dayjs(
                        getTransactionsHistoryParamsRequest?.localToTime
                      ).toDate()
                    : undefined
                }
                onChange={(date) => {
                  if (!date) {
                    // clear date from
                    setGetTransactionsHistoryParamsRequest({
                      ...getTransactionsHistoryParamsRequest,
                      pageNumber: DEFAULT_PAGE,
                      localToTime: undefined,
                    });
                  } else {
                    const selectedDate = dayjs(date).endOf("day");
                    setGetTransactionsHistoryParamsRequest({
                      ...getTransactionsHistoryParamsRequest,
                      pageNumber: DEFAULT_PAGE,
                      localToTime: selectedDate.clone(),
                    });
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
      </div>

      <TableContainer mt="20px">
        <Table data-testid="table-container" variant="striped">
          <Thead backgroundColor={"yellow.500"}>
            <Tr>
              <Th isNumeric color={"white"} w="75px">
                No.
              </Th>
              <Th color={"white"}>Transaction at(UTC+00:00)</Th>
              <Th color={"white"}>Transaction Ref</Th>
              <Th color={"white"}>Amount</Th>
              <Th color={"white"}>Action</Th>
            </Tr>
          </Thead>

          {isLoading && (
            <Tr>
              <Td colSpan={7}>
                <SkeletonText
                  data-testid="skeleton-text"
                  noOfLines={15}
                  spacing="10px"
                  p="10px 0"
                />
              </Td>
            </Tr>
          )}

          {!isLoading && (
            <Tbody>
              {!isGetOrganisationsError &&
                transactionsHistoryData?.data?.map(
                  (transactionHistory, index) => {
                    return (
                      <Tr key={transactionHistory?.txnId}>
                        <Td isNumeric>
                          {getRowIndex(
                            getTransactionsHistoryParamsRequest.pageNumber,
                            getTransactionsHistoryParamsRequest.pageSize,
                            index
                          )}
                        </Td>
                        <Td>
                          {dateFormat(
                            transactionHistory?.txnDateTime,
                            true,
                            FORMATS["YYYY-MM-DD HH:mm"]
                          )}
                        </Td>
                        <Td
                          flexDirection={"row"}
                          flex={"1"}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          <Box
                            overflow={"hidden"}
                            maxW={"5vw"}
                            textOverflow={"ellipsis"}
                          >
                            {transactionHistory?.transactionReference || "-"}
                          </Box>
                          <IconButton
                            variant="unstyled"
                            aria-label="copy-clientSecret"
                            icon={<CopyIcon />}
                            onClick={() => {
                              transactionHistory?.transactionReference &&
                                navigator.clipboard.writeText(
                                  transactionHistory?.transactionReference
                                );
                            }}
                          />
                        </Td>
                        <Td>{`${transactionHistory?.amount?.amount?.toFixed(2) || ""} ${
                          transactionHistory?.amount?.currency || ""
                        }`}</Td>
                        <Td>
                          <Flex gap={"10px"}>
                            {transactionHistory?.paymentNumber && (
                              <Tooltip label="View Payment">
                                <IconButton
                                  data-testid="view-payment-btn"
                                  fontSize="14px"
                                  colorScheme="blue"
                                  aria-label="view-payment"
                                  onClick={() => {
                                    push(
                                      `/payment-detail/${transactionHistory?.paymentNumber}?from=${MerchantViewRef.TRANSACTION_HISTORY}`
                                    );
                                  }}
                                  icon={<PaymentIcon color={"white"} />}
                                />
                              </Tooltip>
                            )}
                             {transactionHistory?.merchantDetails?.merchantID && (
                              <Tooltip label="View Merchant">
                                <IconButton
                                  data-testid="view-merchant-btn"
                                  fontSize="14px"
                                  colorScheme="blue"
                                  aria-label="view-merchant"
                                  onClick={() => {
                                    push(
                                      `/merchant-detail/${transactionHistory?.merchantDetails?.merchantID}?from=${MerchantViewRef.TRANSACTION_HISTORY}`
                                    );
                                  }}
                                  icon={<MerchantIcon color={"white"} />}
                                />
                              </Tooltip>
                            )}
                          </Flex>
                        </Td>
                      </Tr>
                    );
                  }
                )}
            </Tbody>
          )}
        </Table>
      </TableContainer>

      {!isLoading &&
        (transactionsHistoryData?.data?.length === 0 ||
          isGetOrganisationsError) && (
          <Text p="20px" textAlign="center" fontWeight="bold">
            No results found.
          </Text>
        )}

      {!isLoading && (
        <Flex align="center" justifyContent="end" marginTop={"10px"}>
          <Pagination
            totalRecords={transactionsHistoryData?.paging?.totalRecords ?? 0}
            pageSize={getTransactionsHistoryParamsRequest.pageSize}
            onChangePage={(pageNum) =>
              setGetTransactionsHistoryParamsRequest({
                ...getTransactionsHistoryParamsRequest,
                pageNumber: pageNum,
              })
            }
            pageNum={getTransactionsHistoryParamsRequest.pageNumber}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default TransactionHistoryView;
